import { Badge } from '@repletoryreach/sdk-ui';
import type { broadcasts } from '@repletoryreach/sdk-api';
import type { Translate } from '../../../i18n/I18nProvider';

/**
 * How a broadcast is named and marked by channel (4 October 2026).
 *
 * A WhatsApp broadcast is known by its template, as it always was. An SMS
 * broadcast has no template, so it is known by its text where the row carries
 * it (the detail does; the list does not) and by its kind where it does not.
 * An email broadcast is known by its subject, which both the list and the
 * detail carry.
 */
export function isSmsBroadcast(b: broadcasts.Broadcast): boolean {
  return b.channel === 'sms';
}

/** An email broadcast (4 October 2026): no template, a subject instead. */
export function isEmailBroadcast(b: broadcasts.Broadcast): boolean {
  return b.channel === 'email';
}

/** Whether the broadcast is known by a template (WhatsApp) rather than by its own words (SMS, email). */
export function hasTemplate(b: broadcasts.Broadcast): boolean {
  return !isSmsBroadcast(b) && !isEmailBroadcast(b);
}

/** Longest SMS excerpt used as a name, in characters. */
const NAME_LENGTH = 48;

/** The broadcast's name for a heading, a sentence or a screen reader. */
export function broadcastName(t: Translate, b: broadcasts.Broadcast): string {
  if (isEmailBroadcast(b)) {
    const subject = (b.email_subject ?? '').replace(/\s+/g, ' ').trim();
    if (subject) return subject.length > NAME_LENGTH ? `${subject.slice(0, NAME_LENGTH - 1).trimEnd()}…` : subject;
    return emailKindName(t, b);
  }
  if (!isSmsBroadcast(b)) return b.template_code;
  const body = (b.sms_body ?? '').replace(/\s+/g, ' ').trim();
  if (body) return body.length > NAME_LENGTH ? `${body.slice(0, NAME_LENGTH - 1).trimEnd()}…` : body;
  return smsKindName(t, b);
}

/** "Marketing SMS" or "Service SMS". */
export function smsKindName(t: Translate, b: broadcasts.Broadcast): string {
  return b.sms_kind === 'service' ? t('broadcasts.sms.name.service') : t('broadcasts.sms.name.marketing');
}

/** "Marketing email" or "Service email". */
export function emailKindName(t: Translate, b: broadcasts.Broadcast): string {
  return b.email_kind === 'service' ? t('broadcasts.email.name.service') : t('broadcasts.email.name.marketing');
}

/** A small "SMS" or "Email" badge; nothing for a WhatsApp one. */
export function ChannelBadge({ broadcast, t }: { broadcast: broadcasts.Broadcast; t: Translate }) {
  if (isEmailBroadcast(broadcast)) return <Badge size="xs" variant="light" color="grape">{t('broadcasts.email.badge')}</Badge>;
  if (!isSmsBroadcast(broadcast)) return null;
  return <Badge size="xs" variant="light" color="blue">{t('broadcasts.sms.badge')}</Badge>;
}
